import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthService from "../../../../../api/AuthService";
import AccountReconciliationPage from "../../../../../entities/DashboardPage/AccountingModule/BankPage/AccountReconciliationPage";
import LoaderHelper from "../../../../../utils/Loader/LoaderHelper";

function AccountReconciliation() {
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [closingBalance, setClosingBalance] = useState();
  const [transactionData, setTransactionData] = useState([]);
  const [finalizeData, setFinalizeData] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();

  useEffect(() => {
    setClosingBalance(Number(state.closingBalance) || 0);
  }, []);

  useEffect(() => {
    handleGetTransactions();
  }, []);

  useEffect(() => {
    if (state.type === "finalizable") {
      setFinalizeData(true);
    }
  }, []);

  useEffect(() => {
    handleGetCustomers();
    handleGetVendors();
  }, []);

  const handleGetCustomers = async () => {
    LoaderHelper.loaderStatus(true);

    try {
      const result = await AuthService.getCustomer();
      if (result?.success) {
        setCustomers(result?.data?.customers);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleGetVendors = async () => {
    LoaderHelper.loaderStatus(true);

    try {
      const result = await AuthService.getSupplier();
      if (result.success) {
        setVendors(result?.data?.suppliers);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleGetTransactions = async () => {
    LoaderHelper.loaderStatus(true);

    try {
      const result = await AuthService.handleGetAccountTransactionsByDateRange(
        state._id,
        state.startDate,
        state.endDate
      );
      if (result?.success) {
        setTransactionData(result?.data?.transactions);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleAddTransaction = async (formData) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.handleAddAccountTransaction(formData);
      if (result?.success) {
        handleGetTransactions();
        LoaderHelper.loaderStatus(false);
        return { success: true, data: result.data };
      } else {
        LoaderHelper.loaderStatus(false);
        return {
          success: false,
          error: result?.message || "Failed to add transaction",
        };
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleReconcileTransactions = async (formData, draft) => {
    LoaderHelper.loaderStatus(true);

    try {
      // If it's an in-progress draft, redirect immediately
      if (finalizeData && formData?.status === "in-progress" && draft) {
        navigate("/dashboard/accountReconciliationList", {
          state: {
            accountID: state._id,
            bankName: state.bankName,
          },
        });
        return LoaderHelper.loaderStatus(false);
      }

      // Perform finalize or add based on the condition
      const result = finalizeData
        ? await AuthService.handleFinalizeAccountReconciliation(
            state.reconciliationId,
            formData
          )
        : await AuthService.handleAddAccountReconciliation(formData);

      // Handle result
      if (result?.success) {
        navigate("/dashboard/accountReconciliationList", {
          state: {
            accountID: state._id,
            bankName: state.bankName,
          },
        });
      }
    } catch (error) {
      console.error("Reconciliation Error:", error);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  return (
    <AccountReconciliationPage
      accountID={state._id}
      bankName={state.bankName}
      transactionData={transactionData}
      vendors={vendors}
      customers={customers}
      startDate={state.startDate}
      endDate={state.endDate}
      closingBalance={closingBalance}
      setClosingBalance={setClosingBalance}
      handleAddTransaction={handleAddTransaction}
      handleReconcileTransactions={handleReconcileTransactions}
      lastClosingBalance={state.lastClosingBalance}
      finalizeData={finalizeData}
    />
  );
}

export default AccountReconciliation;
